import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DndDropEvent } from '@app/shared/modules/dnd/directives/dnd-dropzone.directive';
import { hexToRGBA } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { ContextMenuComponent, ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import { ProgramPlaylist } from '@radioking/programs';
import {
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import amplitude from 'amplitude-js';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, filter, skip, throttleTime } from 'rxjs/operators';
import { ScrollableLineDirective } from '../../../../../shared/scrollable-line.directive';

import { Program } from '../../models/programs.model';
import {
  ProgramDuplicatePlaylistRequest,
  ProgramEditPlaylistFailure,
  ProgramEditPlaylistRequest,
  ProgramEditPlaylistSuccess,
  ProgramMovePlaylistRequest,
} from '../../states/programs.actions';
import { ProgramAddPlaylistModalComponent } from '../modals/program-add-playlist-modal/program-add-playlist-modal.component';
import { ProgramDeletePlaylistModalComponent } from '../modals/program-delete-playlist-modal/program-delete-playlist-modal.component';
import { ProgramEditPlaylistModalComponent } from '../modals/program-edit-playlist-modal/program-edit-playlist-modal.component';
import { MobProgramDeletePlaylistComponent } from '../sides/mob-program-delete-playlist/mob-program-delete-playlist.component';
import { MobProgramEditPlaylistComponent } from '../sides/mob-program-edit-playlist/mob-program-edit-playlist.component';

@Component({
  selector: 'rk-program-details-list-line',
  templateUrl: './program-details-list-line.component.html',
  styleUrls: ['./program-details-list-line.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgramDetailsListLineComponent
  extends ScrollableLineDirective
  implements OnInit, AfterViewInit
{
  @Input() playlist: ProgramPlaylist;
  @Input() program: Program;
  @Input() new = false;
  @Input()
  resetScrollAxis: Subject<any>;

  isSaving$: Observable<boolean>;

  @ViewChild('line', { static: true })
  line: ElementRef<HTMLDivElement>;

  @ViewChild('ctxMenu')
  contextMenu: ContextMenuComponent;

  hasContextMenuOpen = false;

  newNumber: BehaviorSubject<number>;
  dropBefore = false;
  dragSubject = new BehaviorSubject<DragEvent>(undefined);

  get programId(): number {
    return this.program.id;
  }

  get programPlaylistsNumber(): number {
    return this.program.playlistsNumber;
  }

  constructor(
    protected readonly host: ElementRef,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly uiHelper: UiStoreHelperService,
    private readonly contextMenuService: ContextMenuService,
    private readonly mobileService: MobileSidenavService,
  ) {
    super(host);
    this.iconsNumber = 2;
  }

  ngOnInit(): void {
    this.newNumber = new BehaviorSubject<number>(this.playlist.tracksNumber);
    this.subscription.add(
      this.newNumber.pipe(skip(1), debounceTime(1000)).subscribe(n => {
        this.editPlaylistTracksNumber(n);
      }),
    );
    this.dragSubject
      .asObservable()
      .pipe(
        filter(e => !!e),
        throttleTime(50),
      )
      .subscribe(e => {
        this.moved(e);
      });
    this.isSaving$ = this.uiHelper.getLoadingState(ProgramEditPlaylistRequest, [
      ProgramEditPlaylistSuccess,
      ProgramEditPlaylistFailure,
    ]);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (this.line && this.line.nativeElement) {
      this.line.nativeElement.scrollTop = 0;
      this.ngZone.runOutsideAngular(() => {
        fromEvent<DragEvent>(this.line.nativeElement, 'dragover')
          .pipe(throttleTime(100))
          .subscribe(e => {
            this.dragSubject.next(e);
          });
      });
    }
  }

  editPlaylistTracksNumber(n: number) {
    const playlist: ProgramPlaylist = { ...this.playlist, tracksNumber: n };
    this.editPlaylist(playlist);
  }

  onContextMenu($event: MouseEvent): void {
    this.contextMenuService.show.next({
      item: {
        programId: this.programId,
        playlist: this.playlist,
      },
      contextMenu: this.contextMenu,
      event: $event,
    });
    $event.preventDefault();
    $event.stopPropagation();
  }

  onOpenContextMenu() {
    this.hasContextMenuOpen = true;
  }

  onCloseContextMenu() {
    this.hasContextMenuOpen = false;
  }

  startEditPlaylist() {
    if (window.innerWidth < 768) {
      this.mobileService.open(MobProgramEditPlaylistComponent, {
        bgType: 'gray',
        bottomFullHeight: true,
        data: {
          programId: this.programId,
          playlist: this.playlist,
        },
      });
    } else {
      this.dialog.open(ProgramEditPlaylistModalComponent, {
        data: {
          programId: this.programId,
          playlist: this.playlist,
        },
      });
    }
  }

  squareBGStyle(color: string) {
    return hexToRGBA(color, 0.5);
  }

  squareIconStyle(color: string) {
    return {
      color,
      'border-color': color,
    };
  }

  /**
   * FIXME Unoptimized workaround because api does not handle playlist duplication correctly {@link ProgramsState.programDuplicatePlaylist}
   */
  @Dispatch()
  duplicatePlaylist(targetPosition: 'before' | 'after') {
    amplitude.getInstance().logEvent('duplicate_playlist_program');
    const positionOffset = targetPosition === 'before' ? 0 : 1;
    const position = this.playlist.position + positionOffset; // FIXME Definitive position
    const pPlaylist: Partial<ProgramPlaylist> = {
      idPlaylist: this.playlist.idPlaylist,
      tracksNumber: this.playlist.tracksNumber,
      sorting: this.playlist.sorting,
      position: this.programPlaylistsNumber + 1, // FIXME Temporary position, to mimic "Adding a playlist" behavior
    };

    return new ProgramDuplicatePlaylistRequest(this.programId, pPlaylist, position);
  }

  addPlaylist(targetPosition: 'before' | 'after') {
    const positionOffset = targetPosition === 'before' ? 0 : 1;
    const position = this.playlist.position + positionOffset;
    this.dialog.open(ProgramAddPlaylistModalComponent, {
      data: { program: this.program, position },
    });
  }

  deletePlaylist() {
    if (window.innerWidth < 768) {
      this.mobileService.open(MobProgramDeletePlaylistComponent, {
        data: {
          programId: this.programId,
          playlist: this.playlist,
        },
      });
    } else {
      this.dialog.open(ProgramDeletePlaylistModalComponent, {
        data: {
          programId: this.programId,
          playlistId: this.playlist.idProgramPlaylist,
        },
      });
    }
  }

  @Dispatch()
  editPlaylist(playlist: ProgramPlaylist, moved = false) {
    return moved
      ? new ProgramMovePlaylistRequest(this.programId, playlist)
      : new ProgramEditPlaylistRequest(this.programId, playlist);
  }

  changeTracksNumber(e: Event) {
    this.newNumber.next(Math.min(10000, Number.parseInt(e.target['value'], 10)));
  }

  dndDragImageOffsetFunction(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  dropped(event: DndDropEvent) {
    this.removeAllDragHoverClass();
    this.droppedMovePlaylist(event.data);
  }

  droppedMovePlaylist(playlist: ProgramPlaylist) {
    let realPosition = this.playlist.position + (this.dropBefore ? 0 : 1);
    if (playlist.position < this.playlist.position) {
      realPosition -= 1;
    }
    if (
      playlist.position === this.playlist.position ||
      playlist.position === realPosition
    ) {
      return;
    }
    const playlistNewPosition: ProgramPlaylist = { ...playlist, position: realPosition };
    this.editPlaylist(playlistNewPosition, true);
  }

  moved(event: any) {
    this.ngZone.run(() => {
      const line = event.target.closest('rk-program-details-list-line').firstElementChild;
      this.dropBefore = event.clientY - line.getBoundingClientRect().top < 23;
      this.cdr.detectChanges();
      this.removeAllDragHoverClass(line);
    });
  }

  removeAllDragHoverClass(exception: Element = null) {
    const allLines = document.querySelectorAll(
      'rk-program-details-list-line .line.dnd-dragover',
    );
    allLines.forEach(line => {
      if (!exception || !exception.isSameNode(line)) {
        line.classList.remove('dnd-dragover');
      }
    });
  }
}
